// src/layouts/TestReminderIndicator.js
//
// Campanita que se muestra junto al chip de estado en los dashboards de tests,
// para distinguir de un vistazo qué tests quedaron con recordatorio automático.
//
// Los tests sin recordatorio no renderizan nada, así que la tabla no se ensucia:
// la sola presencia del ícono ya separa los envíos automáticos de los manuales.
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// La API serializa los Timestamp de Firestore como { _seconds, _nanoseconds }.
const toDate = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp._seconds
    ? new Date(timestamp._seconds * 1000)
    : new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWhen = (date) =>
  `${date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} ${date.toLocaleTimeString(
    'es-ES',
    { hour: '2-digit', minute: '2-digit' }
  )}`;

const TestReminderIndicator = ({ test }) => {
  const { t } = useTranslation();

  const config = test && test.reminderConfig;
  if (!config || !config.enabled) return null;

  const sent = Number(test.remindersSent) || 0;
  const max = Number(config.maxReminders) || 0;
  // `nextReminderAt` sólo existe mientras queda un recordatorio pendiente: cuando
  // se agota el tope, el backend borra el campo.
  //
  // La hora se muestra como aproximada a propósito: el cronjob corre cada 30 minutos
  // (:05 y :35), así que el envío real cae en algún punto dentro de esa ventana.
  const next = toDate(test.nextReminderAt);

  let title;
  if (next && sent === 0) {
    const days = Number(config.daysAfterSent) || 0;
    title = t('testReminder.indicatorNext', {
      days: `${days} ${days === 1 ? t('testReminder.day') : t('testReminder.days')}`,
      date: formatWhen(next),
    });
  } else if (next) {
    title = t('testReminder.indicatorProgress', { sent, max, date: formatWhen(next) });
  } else {
    title = t('testReminder.indicatorDone', { sent, max });
  }

  const Icon = next ? NotificationsActiveIcon : NotificationsOffIcon;

  return (
    <Tooltip title={title}>
      <Box component="span" sx={{ display: 'inline-flex', ml: 0.5, verticalAlign: 'middle' }}>
        <Icon
          fontSize="small"
          sx={{ color: next ? 'primary.main' : 'action.disabled' }}
        />
      </Box>
    </Tooltip>
  );
};

export default TestReminderIndicator;
